import { ClickAwayListener, SxProps, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'
import {
  CSSProperties,
  MouseEvent,
  ReactNode,
  useEffect,
  useState,
} from 'react'

import { Option } from 'types/option'

interface Props<TValue> {
  options: () => Option<TValue>[]
  onSelect: (option: Option<TValue>) => void
  children: ReactNode
  emptyText?: string
  render?: (option: Option<TValue>) => ReactNode
}

type Position = {
  x: number
  y: number
}

export default function SimpleSelectorComponent<TValue>(props: Props<TValue>) {
  const [openSelector, setOpenSelector] = useState<Position | null>(null)
  const [options, setOptions] = useState<Option<TValue>[] | null>(null)

  useEffect(() => {
    if (!openSelector) {
      setOptions(null)
    } else {
      setOptions(props.options())
    }
  }, [openSelector, props.options])

  const handleCloseSelector = () => {
    setOpenSelector(null)
  }

  const handleOpenSelector = (e: MouseEvent<HTMLDivElement>) => {
    setOpenSelector({
      x: e.clientX,
      y: e.clientY,
    })
  }

  const handleSelect =
    (option: Option<TValue>) => (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      props.onSelect(option)
      handleCloseSelector()
    }

  const handleClickEmptyText = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    handleCloseSelector()
  }

  return (
    <Box style={selectorContainerStyle} onClick={handleOpenSelector}>
      {openSelector && options != null && (
        <ClickAwayListener onClickAway={handleCloseSelector}>
          {options.length !== 0 ? (
            <Box
              style={{
                ...selectorStyle,
                ...selectorPositionStyle(openSelector),
              }}
            >
              {options.map((option) => (
                <Box
                  key={option.label}
                  onClick={handleSelect(option)}
                  sx={itemContainerStyle}
                >
                  <Box style={itemStyle}>
                    {props.render ? (
                      props.render(option)
                    ) : (
                      <Typography style={itemTextStyle}>
                        {option.label}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              style={{
                ...selectorStyle,
                ...emptyTextStyle,
                ...selectorPositionStyle(openSelector),
              }}
              onClick={handleClickEmptyText}
            >
              {props.emptyText ?? ''}
            </Box>
          )}
        </ClickAwayListener>
      )}
      {props.children}
    </Box>
  )
}

const selectorContainerStyle: CSSProperties = {
  position: 'relative',
  cursor: 'pointer',
}

const selectorStyle: CSSProperties = {
  position: 'fixed',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,

  backgroundColor: 'white',
  border: '1px solid #BDBDBD',
  borderRadius: '4px',
  padding: '4px 0',
  minWidth: '100px',
  maxWidth: '200px',
  maxHeight: '200px',
  overflowY: 'scroll',
}

const emptyTextStyle: CSSProperties = {
  padding: '4px',
  width: 'fit-content',
  maxWidth: '200px',
  overflow: 'hidden',
}

const selectorPositionStyle = (position: Position): CSSProperties => ({
  left: position.x,
  top: position.y,
})

const itemContainerStyle: SxProps<Theme> = {
  padding: '0 8px',
  '&:hover': {
    cursor: 'pointer',
    backgroundColor: '#F5F5F5',
  },
}

const itemStyle: CSSProperties = {
  // display: 'flex',
  // justifyContent: 'space-between',
  // textDecoration: 'underline',
  padding: '4px',
  borderBottom: '1px solid #BDBDBD',
}

const itemTextStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  textDecoration: 'underline',
}
